import { motion, useReducedMotion } from 'motion/react'
import { cn } from '@/lib/utils'
import { normalizeOrderTypeId } from '@/lib/orderTypeSettings'

type GlyphProps = {
  active: boolean
  className?: string
  /** Used for generic custom types (stroke inherits via currentColor from parent) */
  accentHex?: string
}

/**
 * Motion-animated order-type glyphs (lucide-animated style): Lucide-grade
 * artwork drawn inline so individual parts can move. Loops only run while the
 * segment is selected, a soft scale "pop" plays on activation, and everything
 * goes static under prefers-reduced-motion. Strokes inherit `currentColor`
 * from the segment, so configured order-type colors keep working.
 */

const strokeProps = {
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  fill: 'none',
} as const

function useGlyphMotion(active: boolean) {
  const reduced = useReducedMotion()
  return {
    animate: !reduced && active,
    pop:
      !reduced && active
        ? { scale: [1, 1.12, 1], transition: { duration: 0.45, ease: 'easeOut' as const } }
        : { scale: 1 },
  }
}

/** Walk-in — shop storefront: the awning ripples while selected. */
function WalkInGlyph({ active, className }: GlyphProps) {
  const { animate, pop } = useGlyphMotion(active)
  return (
    <motion.svg
      viewBox="0 0 24 24"
      className={cn('h-6 w-6 shrink-0', className)}
      aria-hidden
      animate={pop}
    >
      {/* awning */}
      <motion.g
        style={{ transformOrigin: '12px 4px' }}
        animate={animate ? { scaleY: [1, 1.08, 0.96, 1] } : { scaleY: 1 }}
        transition={
          animate
            ? { duration: 1.8, repeat: Infinity, ease: 'easeInOut' }
            : { duration: 0.2 }
        }
      >
        <path d="M3 9 4.5 4h15L21 9" {...strokeProps} />
        <path d="M3 9a2.25 2.25 0 0 0 4.5 0 2.25 2.25 0 0 0 4.5 0 2.25 2.25 0 0 0 4.5 0A2.25 2.25 0 0 0 21 9" {...strokeProps} />
      </motion.g>
      {/* shop body + door */}
      <path d="M5 11.5V20h14v-8.5" {...strokeProps} />
      <path d="M10 20v-5h4v5" {...strokeProps} strokeWidth={1.5} />
    </motion.svg>
  )
}

/** Pickup — shopping bag swinging from its handle. */
function PickupGlyph({ active, className }: GlyphProps) {
  const { animate, pop } = useGlyphMotion(active)
  return (
    <motion.svg
      viewBox="0 0 24 24"
      className={cn('h-6 w-6 shrink-0', className)}
      aria-hidden
      animate={pop}
    >
      <motion.g
        style={{ transformOrigin: '12px 4.5px' }}
        animate={animate ? { rotate: [0, -5, 4, -2, 0] } : { rotate: 0 }}
        transition={
          animate
            ? { duration: 2.2, repeat: Infinity, ease: 'easeInOut' }
            : { duration: 0.2 }
        }
      >
        <path d="M7 4 4.5 7.5v12a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2v-12L17 4Z" {...strokeProps} />
        <path d="M4.5 7.5h15" {...strokeProps} />
        <path d="M15.5 11.5a3.5 3.5 0 0 1-7 0" {...strokeProps} />
      </motion.g>
    </motion.svg>
  )
}

/** Delivery — courier bike bobbing along with speed lines. */
function DeliveryGlyph({ active, className }: GlyphProps) {
  const { animate, pop } = useGlyphMotion(active)
  return (
    <motion.svg
      viewBox="0 0 24 24"
      className={cn('h-6 w-6 shrink-0', className)}
      aria-hidden
      animate={pop}
    >
      {/* speed lines */}
      {[
        { d: 'M1.5 10.5h3', delay: 0 },
        { d: 'M1 14h2.2', delay: 0.4 },
      ].map((s) => (
        <motion.path
          key={s.d}
          d={s.d}
          {...strokeProps}
          strokeWidth={1.5}
          animate={animate ? { x: [1.5, -1.5], opacity: [0, 1, 0] } : { opacity: 0 }}
          transition={
            animate
              ? { duration: 0.9, repeat: Infinity, ease: 'linear', delay: s.delay }
              : { duration: 0.2 }
          }
        />
      ))}
      {/* bike bobs as it rides */}
      <motion.g
        animate={animate ? { y: [0, -0.8, 0] } : { y: 0 }}
        transition={
          animate
            ? { duration: 1.1, repeat: Infinity, ease: 'easeInOut' }
            : { duration: 0.2 }
        }
      >
        <circle cx="18.5" cy="17.5" r="3.5" {...strokeProps} />
        <circle cx="5.5" cy="17.5" r="3.5" {...strokeProps} />
        <circle cx="15" cy="5" r="1" {...strokeProps} />
        <path d="M12 17.5V14l-3-3 4-3 2 3h2" {...strokeProps} />
      </motion.g>
    </motion.svg>
  )
}

/** Fallback for custom order types — parcel with a soft pulse. */
function GenericGlyph({ active, className }: GlyphProps) {
  const { animate, pop } = useGlyphMotion(active)
  return (
    <motion.svg
      viewBox="0 0 24 24"
      className={cn('h-6 w-6 shrink-0', className)}
      aria-hidden
      animate={pop}
    >
      <motion.g
        style={{ transformOrigin: '12px 12px' }}
        animate={animate ? { scale: [1, 1.05, 1] } : { scale: 1 }}
        transition={
          animate
            ? { duration: 1.8, repeat: Infinity, ease: 'easeInOut' }
            : { duration: 0.2 }
        }
      >
        <path d="M12 4 4 8v8l8 4 8-4V8l-8-4Z" {...strokeProps} />
        <path d="M4 8l8 4 8-4M12 12v8" {...strokeProps} strokeWidth={1.5} />
      </motion.g>
    </motion.svg>
  )
}

export function CounterOrderTypeGlyph({
  type,
  active,
  className,
}: GlyphProps & { type: string }) {
  const t = normalizeOrderTypeId(type)
  if (t === 'walk_in') return <WalkInGlyph active={active} className={className} />
  if (t === 'pickup') return <PickupGlyph active={active} className={className} />
  if (t === 'delivery') return <DeliveryGlyph active={active} className={className} />
  return <GenericGlyph active={active} className={className} />
}
